import { type CaseDto, type CaseMaterialsDto, datePl, type MeDto } from '@bqcrm/shared';
import { Alert, Button, Card, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconPlus, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { can } from '../../auth';
import { PackageStatusBadge, ReadinessBadge, TradeBadge } from '../../components/Badges';
import { Anchor } from '@mantine/core';
import { Link } from 'react-router';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { ExtraMaterialModal } from '../warehouse/ExtraMaterialModal';
import { DemandHeader, DemandLinesTable } from '../warehouse/demand-ui';

const STATE_LABEL: Record<CaseMaterialsDto['scopes'][number]['demandState'], string> = {
  brak_umowy: 'brak umowy',
  czeka_na_wyzwalacz: 'potrzeby powstaną po zdarzeniu z ustawień (podpis / warunki startu)',
  utworzone: 'potrzeby z umowy utworzone',
  reczne: 'potrzeby tworzy magazynier ręcznie',
};

/**
 * Materiały sprawy (docs/14 §3): wymagane, zarezerwowane, braki, wydane, gotowość, wartość materiałów wydanych
 * na realizację (z uprawnieniem do cen zakupu), planowany termin realizacji zakresów.
 */
export function MaterialsTab({ c, me }: { c: CaseDto; me: MeDto }) {
  const q = useQuery({ queryKey: ['case-materials', c.id], queryFn: () => api<CaseMaterialsDto>(`/cases/${c.id}/materials`) });
  const qc = useQueryClient();
  const set = (r: CaseMaterialsDto) => qc.setQueryData(['case-materials', c.id], r);
  const sync = useMutation({
    mutationFn: () => api<CaseMaterialsDto>(`/cases/${c.id}/materials/sync`, { method: 'POST' }),
    onSuccess: (r) => (set(r), notifyOk('Przeliczono potrzeby materiałowe')),
    onError: notifyError,
  });
  const fromContract = useMutation({
    mutationFn: (contractId: string) => api<CaseMaterialsDto>(`/contracts/${contractId}/demands`, { method: 'POST' }),
    onSuccess: (r) => (set(r), notifyOk('Utworzono potrzeby z umowy')),
    onError: notifyError,
  });
  const m = q.data;
  const manage = can(me, 'magazyn.rezerwacje');
  const [extra, setExtra] = useState(false);
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {m && (
        <Stack>
          <Group justify="space-between" wrap="wrap">
            <Group gap="xs">
              <Text fw={600}>Gotowość materiałowa:</Text>
              {m.readiness ? <ReadinessBadge readiness={m.readiness} /> : <Text c="dimmed">brak potrzeb</Text>}
              {m.issuedValueGr !== null && (
                <Text size="sm" c="dimmed">
                  · wartość materiałów wydanych na realizację: <Money gr={m.issuedValueGr} />
                </Text>
              )}
            </Group>
            <Group gap="xs">
              {can(me, 'zapotrzebowania.zglaszanie') && !c.closedAt && (
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setExtra(true)}>
                  Materiał dodatkowy
                </Button>
              )}
              {m.canSync && (
                <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => sync.mutate()} loading={sync.isPending}>
                  Przelicz potrzeby
                </Button>
              )}
            </Group>
          </Group>
          <SectionCard title="Zakresy" padding={0}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Branża</Table.Th>
                  <Table.Th>Umowa</Table.Th>
                  <Table.Th>Planowany termin realizacji</Table.Th>
                  <Table.Th>Potrzeby z umowy</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {m.scopes.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td>
                      <TradeBadge code={s.tradeCode} />
                    </Table.Td>
                    <Table.Td>{s.contractNumber ?? '—'}</Table.Td>
                    <Table.Td>{m.canEditPlannedDate ? <PlannedDate scopeId={s.id} value={s.plannedDate} onSaved={set} /> : datePl(s.plannedDate)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          {STATE_LABEL[s.demandState]}
                        </Text>
                        {m.canSync && s.contractId && (s.demandState === 'reczne' || s.demandState === 'czeka_na_wyzwalacz') && (
                          <Button
                            size="compact-xs"
                            variant="light"
                            loading={fromContract.isPending}
                            onClick={() => fromContract.mutate(s.contractId!)}
                          >
                            Utwórz teraz
                          </Button>
                        )}
                      </Group>
                      {s.unlinkedItems.length > 0 && (
                        <Text size="xs" c="orange" mt={2} title={s.unlinkedItems.join('\n')}>
                          Usługi bez katalogu (nie tworzą potrzeb): {s.unlinkedItems.slice(0, 3).join(', ')}
                          {s.unlinkedItems.length > 3 ? ` i ${s.unlinkedItems.length - 3} więcej` : ''} — jeśli to materiał, dopisz go przyciskiem „Materiał dodatkowy”
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </SectionCard>
          {m.packages.length > 0 && (
            <SectionCard title="Pakiety dla ekip" padding={0}>
              <Table>
                <Table.Tbody>
                  {m.packages.map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td>
                        {can(me, 'magazyn.podglad') ? (
                          <Anchor component={Link} to={`/magazyn/pakiety?pakiet=${p.id}`} size="sm" fw={600}>
                            {p.number}
                          </Anchor>
                        ) : (
                          <Text size="sm" fw={600}>
                            {p.number}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed">
                          {p.taskNumber ?? ''} {p.plannedPickup ? `· odbiór ${datePl(p.plannedPickup)}` : ''} {p.pickupPlace ? `· ${p.pickupPlace}` : ''}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{p.lines.map((l) => `${l.name}: ${l.picked + l.issued}/${l.required}`).join(', ')}</Text>
                      </Table.Td>
                      <Table.Td>
                        <PackageStatusBadge status={p.status} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </SectionCard>
          )}
          {m.demands.length === 0 ? (
            <EmptyState title="Brak zapotrzebowań" hint="Potrzeby z umowy powstaną po podpisaniu umowy (albo po spełnieniu warunków startu — wg ustawień firmy). Ekipa zgłasza materiały w zadaniu w aplikacji mobilnej." />
          ) : (
            m.demands.map((d) => (
              <Card key={d.id} withBorder padding="sm">
                <Stack gap="xs">
                  <DemandHeader d={d} linkCase={false} />
                  {d.comment && (
                    <Alert variant="light" color="gray" p="xs">
                      {d.requestedByName ? `${d.requestedByName}: ` : ''}
                      {d.comment}
                    </Alert>
                  )}
                  <DemandLinesTable d={d} manage={manage} warehouseId={d.warehouseId} />
                </Stack>
              </Card>
            ))
          )}
        </Stack>
      )}
      {extra && <ExtraMaterialModal caseId={c.id} onClose={() => setExtra(false)} />}
    </QueryState>
  );
}

function PlannedDate({ scopeId, value, onSaved }: { scopeId: string; value: string | null; onSaved: (r: CaseMaterialsDto) => void }) {
  const [v, setV] = useState(value ?? '');
  const save = useMutation({
    mutationFn: () => api<CaseMaterialsDto>(`/scopes/${scopeId}/planned-date`, { method: 'PUT', body: { plannedDate: v || null } }),
    onSuccess: (r) => (onSaved(r), notifyOk('Zapisano planowany termin')),
    onError: notifyError,
  });
  return (
    <Group gap={4} wrap="nowrap">
      <TextInput size="xs" type="date" value={v} onChange={(e) => setV(e.currentTarget.value)} w={150} />
      {v !== (value ?? '') && (
        <Button size="compact-xs" onClick={() => save.mutate()} loading={save.isPending}>
          Zapisz
        </Button>
      )}
    </Group>
  );
}
